import { useCallback, useEffect, useRef, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import { cacheScope, readCache } from "@/lib/ui-cache";
import { useCacheScope } from "@/hooks/use-cache-scope";
import type { UnifiedPreferencesSnapshot } from "@/lib/unified-preferences";

import { type AccessTokenGetter } from "@/lib/utils";
import i18n from "@/lib/i18n";
import {
  patchUnifiedPreferences,
  readUnifiedPreferences,
} from "@/lib/unified-preferences";

function uniqueAgentIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const item of value) {
    if (typeof item !== "string" || !item || seen.has(item)) continue;
    seen.add(item);
    result.push(item);
  }
  return result;
}

export interface UseAgentPinsReturn {
  /** Raw persisted IDs, including IDs that may be inaccessible in this UI. */
  agentIds: string[];
  loading: boolean;
  /** True only after at least one successful preference GET. */
  loaded: boolean;
  /** Timestamp of the most recent successful GET or confirmed mutation. */
  updatedAt: number | null;
  /** GET-specific error used by consumers that need a complete pin snapshot. */
  loadError: string | null;
  error: string | null;
  refresh: () => Promise<string[] | null>;
  pin: (agentId: string) => Promise<string[] | null>;
  unpin: (agentId: string) => Promise<string[] | null>;
}

/**
 * Server-backed agent pin preference state. Mutations are optimistic, while a
 * monotonic operation generation prevents slower responses from replacing a
 * newer desired local sequence.
 */
export function useAgentPins(getToken: AccessTokenGetter): UseAgentPinsReturn {
  const verifiedScope = useCacheScope();
  const previousScopeRef = useRef(verifiedScope);
  const [agentIds, setAgentIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const agentIdsRef = useRef<string[]>([]);
  const operationGenerationRef = useRef(0);
  const refreshInFlightRef = useRef<Promise<string[] | null> | null>(null);
  const liveRef = useRef(false);

  const refresh = useCallback((): Promise<string[] | null> => {
    if (refreshInFlightRef.current) return refreshInFlightRef.current;

    const request = (async (): Promise<string[] | null> => {
      const operation = operationGenerationRef.current + 1;
      operationGenerationRef.current = operation;
      setLoading(true);
      setError(null);
      setLoadError(null);
      try {
        const next = uniqueAgentIds(
          (await readUnifiedPreferences(getToken)).agentPins,
        );
        if (operation !== operationGenerationRef.current) return null;
        agentIdsRef.current = next;
        setAgentIds(next);
        setLoaded(true);
        liveRef.current = true;
        setUpdatedAt(Date.now());
        return next;
      } catch (err) {
        if (operation !== operationGenerationRef.current) return null;
        const message = err instanceof Error ? err.message : i18n.t("sidebar.pinnedAgentsLoadFailed");
        setError(message);
        setLoadError(message);
        return null;
      } finally {
        if (operation === operationGenerationRef.current) setLoading(false);
      }
    })();

    refreshInFlightRef.current = request;
    void request.finally(() => {
      if (refreshInFlightRef.current === request) {
        refreshInFlightRef.current = null;
      }
    });
    return request;
  }, [getToken]);

  const mutate = useCallback(async (
    method: "PUT" | "DELETE",
    agentId: string,
  ): Promise<string[] | null> => {
    const normalizedAgentId = agentId.trim();
    if (!normalizedAgentId) return null;

    const operation = operationGenerationRef.current + 1;
    operationGenerationRef.current = operation;
    const prior = agentIdsRef.current;
    const desired = method === "PUT"
      ? prior.includes(normalizedAgentId)
        ? prior
        : [...prior, normalizedAgentId]
      : prior.filter((id) => id !== normalizedAgentId);
    agentIdsRef.current = desired;
    setError(null);
    setAgentIds(desired);

    try {
      const next = uniqueAgentIds(
        (await patchUnifiedPreferences(getToken, { agentPins: desired })).agentPins,
      );
      if (operation !== operationGenerationRef.current) return null;
      agentIdsRef.current = next;
      setAgentIds(next);
      setUpdatedAt(Date.now());
      // A successful mutation necessarily confirms the preference relation,
      // but it is intentionally not used as the successful *initial GET*
      // signal for ChatPage's default-pin bootstrap.
      return next;
    } catch (err) {
      if (operation !== operationGenerationRef.current) return null;
      agentIdsRef.current = prior;
      setAgentIds(prior);
      setError(err instanceof Error ? err.message : i18n.t("sidebar.errors.updateAgentPins"));
      return null;
    }
  }, [getToken]);

  const pin = useCallback((agentId: string) => mutate("PUT", agentId), [mutate]);
  const unpin = useCallback((agentId: string) => mutate("DELETE", agentId), [mutate]);

  useRefreshOnReconnect(refresh);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (previousScopeRef.current === verifiedScope) return;
    previousScopeRef.current = verifiedScope;
    setAgentIds([]); setLoaded(false); liveRef.current = false;
    refreshInFlightRef.current = null;
    if (!verifiedScope) return;
    let cancelled = false;
    void readCache<UnifiedPreferencesSnapshot>("displayPrefs", "display", "settings").then((snapshot) => {
      if (cancelled || liveRef.current || cacheScope() !== verifiedScope || !snapshot) return;
      const preview = uniqueAgentIds(snapshot.agentPins);
      agentIdsRef.current = preview;
      setAgentIds(preview);
    });
    void refresh();
    return () => { cancelled = true; operationGenerationRef.current += 1; };
  }, [verifiedScope]);

  return {
    agentIds,
    loading,
    loaded,
    updatedAt,
    loadError,
    error,
    refresh,
    pin,
    unpin,
  };
}
